import { useEffect } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import {
	isBlogPage,
	isDatabase,
	isSpaceOverview,
	isViewPage,
	isWhiteboard,
} from '@confluence/named-routes';
import { usePageState } from '@confluence/page-context';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { expValNoExposure } from '@confluence/feature-experiments';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState';

import { useImproveInviteEmailProductContextActions } from './useImproveInviteEmailProductContextState';
import type { ContentHeaderQuery as ContentHeaderQueryType } from './__types__/ContentHeaderQuery';
import { ContentHeaderQuery } from './ContentHeaderQuery.graphql';

const useIsEligiblePage = () => {
	const isEditorPage = useIsEditorPage();
	const isViewPageRoute = isViewPage();
	const isSpaceOverviewRoute = isSpaceOverview();
	const isBlogPageRoute = isBlogPage();
	const isDatabaseRoute = isDatabase();
	const isWhiteboardRoute = isWhiteboard();
	return {
		onContentPage:
			isViewPageRoute ||
			isSpaceOverviewRoute ||
			isBlogPageRoute ||
			isDatabaseRoute ||
			isWhiteboardRoute ||
			isEditorPage,
	};
};

const titleFromMetadata = (data: Record<string, string | number | boolean>) => {
	if (!data || !('title' in data) || typeof data.title !== 'string') {
		return null;
	}
	const { title } = data;
	return title;
};

export const useImproveInviteEmailFetchHook = () => {
	const { setInviteContext } = useImproveInviteEmailProductContextActions();
	const { onContentPage } = useIsEligiblePage();
	const [{ collabEditProvider }] = useNativeCollabState();

	const [{ contentId, contentType }] = usePageState();

	const { data: queryData } = useQueryInterop<ContentHeaderQueryType>(ContentHeaderQuery, {
		variables: {
			contentId,
		},
		fetchPolicy: 'cache-and-network',
		skip:
			!contentId ||
			!onContentPage ||
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation -- Exposed in post office
			!expValNoExposure('cfind-3976-confluence-invite-emails', 'enrolled', false),
	});

	useEffect(() => {
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation -- Exposed in post office
		if (!expValNoExposure('cfind-3976-confluence-invite-emails', 'enrolled', false)) {
			return;
		}

		if (onContentPage) {
			const editorTitle = collabEditProvider
				? titleFromMetadata(collabEditProvider.getMetadata())
				: null;

			setInviteContext({
				pageTitle: editorTitle ?? queryData?.content?.nodes?.[0]?.title,
				type: contentType,
				createdBy:
					queryData?.content?.nodes?.[0]?.history?.createdBy?.displayName ??
					queryData?.content?.nodes?.[0]?.version?.by?.displayName,
				lastEditedTime:
					queryData?.content?.nodes?.[0]?.metadata?.lastModifiedDate ??
					queryData?.content?.nodes?.[0]?.version?.when,
			} as Record<string, string>);
		} else {
			setInviteContext(undefined);
		}
	}, [onContentPage, queryData, contentType, setInviteContext, collabEditProvider]);
};
